import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {clsx} from 'clsx'
import {getBackgroundColor} from '../diff-line-helpers'
import type {SimpleDiffLine} from '../types'
import {HunkKebabIcon} from './HunkKebabIcon'
import {UnifiedDiffTable} from './UnifiedDiffTable'
import styles from './UnifiedDiffLines.module.css'

/**
 * Presentational component for a non-interactive line number cell
 */
function LineNumberCell({line, children}: {line: SimpleDiffLine; children: React.ReactNode}) {
  return (
    <td className={styles.diffLineNumber} style={{backgroundColor: getBackgroundColor(line.type, true)}}>
      <code>{children}</code>
    </td>
  )
}

/**
 * Presentational component for a non-interactive hunk cell
 * Note that it spans the entire table row
 */
function HunkCell({line, columnCount}: {line: SimpleDiffLine; columnCount: number}) {
  return (
    <td colSpan={columnCount} className={styles.diffHunkCell} valign="top">
      <div className="d-flex flex-row">
        <HunkKebabIcon />
        <code className={styles.diffHunkText}>
          {/* Explicitly mark html as safe because it is server-sanitized */}
          <VerifiedHTMLDiv
            className={clsx(styles.diffTextInner, 'color-fg-muted')}
            /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
            html={line.html as SafeHTMLString}
          />
        </code>
      </div>
    </td>
  )
}

/**
 * Renders a non-interactive row of a unified diff line
 * Specifically for use in outdated threads, which return a slightly different set of difflines
 * This component was derived from the interactive version (CodeDiffLine) in DiffLineTableRow.tsx
 */
function UnifiedDiffRow({
  line,
  showLeftLineNumbers,
  showRightLineNumbers,
}: {
  line: SimpleDiffLine
  showLeftLineNumbers: boolean
  showRightLineNumbers: boolean
}) {
  const isHunkRow = line.type === 'HUNK'
  const showLeftNumber = line.type !== 'ADDITION'
  const showRightNumber = line.type !== 'DELETION'

  const lineMarker = line.type === 'ADDITION' ? '+' : line.type === 'DELETION' ? '-' : undefined
  const showLineMarker = !!lineMarker

  // Strip leading +/- from HTML if present, since we render the marker separately.
  // The server includes the +/- prefix in line.html (matching DiffText behavior).
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments */
  let lineHTML: SafeHTMLString = line.html as SafeHTMLString
  if (showLineMarker && lineHTML[0] === lineMarker) {
    /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments */
    lineHTML = lineHTML.slice(1) as SafeHTMLString
  }

  return (
    <tr>
      {isHunkRow && (
        <HunkCell line={line} columnCount={1 + Number(showLeftLineNumbers) + Number(showRightLineNumbers)} />
      )}
      {!isHunkRow && (
        <>
          {showLeftLineNumbers && <LineNumberCell line={line}>{showLeftNumber && line.left}</LineNumberCell>}
          {showRightLineNumbers && <LineNumberCell line={line}>{showRightNumber && line.right}</LineNumberCell>}
          <td className={styles.diffTextCell} style={{backgroundColor: getBackgroundColor(line.type, false)}}>
            <code
              className={clsx(
                line.type === 'ADDITION' && styles.syntaxHighlightedAdditionLine,
                line.type === 'DELETION' && styles.syntaxHighlightedDeletionLine,
              )}
            >
              {showLineMarker && <span className={styles.diffTextMarker}>{lineMarker}</span>}
              {/* Explicitly mark html as safe because it is server-sanitized */}
              <VerifiedHTMLDiv
                className={styles.diffTextInner}
                html={lineHTML}
                style={{
                  backgroundColor: getBackgroundColor(line.type, false),
                }}
              />
            </code>
          </td>
        </>
      )}
    </tr>
  )
}

export function UnifiedDiffLines({
  className,
  lines,
  lineWidth,
  tabSize,
}: {
  className?: string
  lines: SimpleDiffLine[]
  lineWidth: string
  tabSize: number
}) {
  const showLeftLineNumbers = lines.some(line => line.type !== 'HUNK' && line.type !== 'ADDITION' && line.left !== null)
  const showRightLineNumbers = lines.some(
    line => line.type !== 'HUNK' && line.type !== 'DELETION' && line.right !== null,
  )

  const diffRows = lines.map((diffline, index) => {
    const line = diffline
    return (
      <UnifiedDiffRow
        // eslint-disable-next-line @eslint-react/no-array-index-key
        key={index}
        line={line}
        showLeftLineNumbers={showLeftLineNumbers}
        showRightLineNumbers={showRightLineNumbers}
      />
    )
  })

  return (
    <table
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className={clsx(styles.unifiedDiffLines, className, 'tab-size')}
      data-tab-size={tabSize}
    >
      <UnifiedDiffTable
        lineWidth={lineWidth}
        showLeftLineNumbers={showLeftLineNumbers}
        showRightLineNumbers={showRightLineNumbers}
      >
        {diffRows}
      </UnifiedDiffTable>
    </table>
  )
}
